<template>
    <!-- Block -->
    <v-col
        class="d-flex flex-column fill-height"
        :class="[
            `theme--${theme}`,
            `content-block content-block-${block.id} content-block-${breakpointName}`,
            { 'content-block-border-top': block.dividerLine.top },
            { 'content-block-border-bottom': block.dividerLine.bottom },
            { 'content-block-border-start': block.dividerLine.start && breakpoint === 'desktop' },
            { 'content-block-border-end': block.dividerLine.end && breakpoint === 'desktop' },
            ...block.appearance.base.cssClass,
            ...block.appearance.theme[theme].cssClass,
            ...appearance.cssClass
        ]"
        :style="{
            ...block.appearance.base.cssStyle,
            ...block.appearance.theme[theme].cssStyle,
            ...appearance.cssStyle
        }"
    >
        <component
            v-if="background"
            :is="'style'"
        >
            {{ background }}
        </component>
        <slot name="default">
            <BtnUniversal
                :params="block.hyperlinkBlock"
                :disable-link="!block.hyperlinkBlock"
                class="container-grid content-block-items no-style-link pa-0 fill-width position-relative"
                :class="{
                    'container-grid--fluid': !block.constrainedWidth,
                    'fill-height': block.fillHeight || !isMobile
                }"
                nuxt-link
                plain-link
            >
                <v-row
                    class="fill-width flex-grow-0 flex-shrink-1"
                    :class="[
                        block.row.cssClass,
                        { 'fill-height': block.fillHeight || !isMobile },
                        ...appearanceRow.cssClass
                    ]"
                    :style="{
                        ...block.row.cssStyle,
                        ...appearanceRow.cssStyle
                    }"
                    :dense="!!block.row.dense"
                    :no-gutters="block.row.noGutters || true"
                    :align="(block.row.align as any)"
                    :align-content="(block.row.alignContent as any)"
                    :align-content-sm="(block.row.alignContentSm as any)"
                    :align-content-md="(block.row.alignContentMd as any)"
                    :align-content-lg="(block.row.alignContentLg as any)"
                    :align-content-xl="(block.row.alignContentXl as any)"
                    :align-sm="(block.row.alignSm as any)"
                    :align-md="(block.row.alignMd as any)"
                    :align-lg="(block.row.alignLg as any)"
                    :align-xl="(block.row.alignXl as any)"
                    :justify="(block.row.justify as any)"
                    :justify-sm="(block.row.justifySm as any)"
                    :justify-md="(block.row.justifyMd as any)"
                    :justify-lg="(block.row.justifyLg as any)"
                    :justify-xl="(block.row.justifyXl as any)"
                >
                    <!-- Sub-Block -->
                    <ContentSubBlock
                        v-for="(subBlock, i) in block.blocks"
                        :key="i"
                        :id="`${block.id}`"
                        :sub-block="subBlock"
                        :theme="theme"
                        :breakpoint="breakpoint"
                        :breakpoint-name="breakpointName"
                        :is-mobile="isMobile"
                        :is-constrained-width="block.constrainedWidth"
                        :is-hyperlink-block="!!block.hyperlinkBlock"
                    ></ContentSubBlock>
                </v-row>
            </BtnUniversal>
        </slot>
    </v-col>
</template>

<script lang="ts" setup>
    import type {
        ContentBlockComponentProps,
        BreakpointType
    } from '~/types';
    import { ContentBlockThemeStyleItems } from '~/types';
    import BtnUniversal from '~/components/BtnUniversal/BtnUniversal.vue';
    import ContentSubBlock from '~/components/content/ContentSubBlock/ContentSubBlock.vue';
    import { legacyContentHelpers } from '~/composables';
    import {
        useCdnResource
    } from 'x1-base/composables';

    const props = withDefaults(
        defineProps<ContentBlockComponentProps>(),
        {
            theme: 'light',
            breakpoint: 'desktop',
            breakpointName: 'md'
        }
    );

    const useCdnResourceObj = useCdnResource();
    const { getAppearance, processBackground, getEmptyThemeStyleObj } = legacyContentHelpers;
    const blockClassName = `content-block-${props.block.id}`;
    const appearance = computed<ContentBlockThemeStyleItems>(() => {
        const current: BreakpointType = props.breakpointName;
        const appearance = props.block.appearance;

        return getAppearance(appearance, current);
    });

    const appearanceRow = computed<ContentBlockThemeStyleItems>(() => {
        const current: BreakpointType = props.breakpointName;
        const appearance = props.block.row.appearance;

        if (!appearance) {
            return getEmptyThemeStyleObj();
        }

        return getAppearance(appearance, current);
    });

    const background = computed<string>(() =>
        processBackground(blockClassName, props.block?.background, props.theme, useCdnResourceObj.cdnBasePathMain)
    );
</script>

<!-- Styles in /assets/styles/common.scss -->
<style lang="scss"></style>
